import { parseISO, isValid } from 'date-fns';
const isISO = (value) => {
    if (typeof value !== 'string')
        return false;
    try {
        const parsed = parseISO(value);
        return isValid(parsed) && parsed.toISOString().startsWith(value);
    }
    catch {
        return false;
    }
};
/**
 * Traverses a wizard flow from a start step to an end step, collecting all answers along the way.
 *
 * This function follows the nextStep chain dynamically, handling both static step IDs and
 * conditional routing functions. It's useful for collecting all answers in a specific section
 * of a wizard, such as the "reason flow" between reason-selection and goal-selection.
 *
 * @param flow - The wizard flow definition containing all steps
 * @param answers - The current wizard answers
 * @param startStepId - The step ID to start traversing from (default: 'reason-selection')
 * @param endStepId - The step ID to stop at (not included in results, default: 'goal-selection')
 * @param translateFn - Optional translation function to format questions and answers
 * @param formatContext - Optional context for formatting (employee info, date formatter)
 * @returns Array of step-answer pairs for all steps in the traversal path
 *
 * @example
 * ```typescript
 * const reasonFlowAnswers = traverseReasonFlow(
 *   feedbackWizardFlow,
 *   answers,
 *   'reason-selection',
 *   'goal-selection',
 *   t, // translation function
 *   { selectedEmployee, formatDate }
 * );
 * // Returns: [
 * //   {
 * //     stepId: 'reason-selection',
 * //     answer: ['collaboration-service'],
 * //     formattedQuestion: 'What was the reason?',
 * //     formattedAnswer: 'Collaboration/Service'
 * //   },
 * //   ...
 * // ]
 * ```
 */
export function traverseReasonFlow(flow, answers, startStepId = 'reason-selection', endStepId = 'goal-selection', translateFn, formatContext) {
    const result = [];
    // Create a map of steps by ID for quick lookup
    const stepMap = new Map();
    for (const step of flow.steps) {
        stepMap.set(step.id, step);
    }
    let currentStepId = startStepId;
    const visitedSteps = new Set(); // Prevent infinite loops
    while (currentStepId !== null && currentStepId !== endStepId) {
        // Check for infinite loop
        if (visitedSteps.has(currentStepId)) {
            console.warn(`Infinite loop detected in wizard flow at step: ${String(currentStepId)}`);
            break;
        }
        visitedSteps.add(currentStepId);
        // Get the current step
        const currentStep = stepMap.get(currentStepId);
        if (!currentStep) {
            console.warn(`Step not found in flow: ${String(currentStepId)}`);
            break;
        }
        // Get the answer for this step
        const answer = answers[currentStepId];
        // Only include steps that have answers
        if (answer !== undefined && answer !== null) {
            const stepAnswer = {
                stepId: String(currentStepId),
                answer: answer,
            };
            // Format question and answer if translation function is provided
            if (translateFn && formatContext) {
                stepAnswer.formattedQuestion = formatStepTitle(currentStep, answers, translateFn, formatContext, String(currentStepId));
                stepAnswer.formattedAnswer = formatAnswerValue(answers, answer, currentStep, stepMap, translateFn, formatContext);
            }
            result.push(stepAnswer);
        }
        // Determine the next step
        if (!currentStep.nextStep) {
            // No next step defined, end of flow
            break;
        }
        if (typeof currentStep.nextStep === 'function' &&
            answer !== undefined &&
            answer !== null) {
            // Dynamic routing based on answers
            const nextStepResult = currentStep.nextStep(answer, answers);
            currentStepId = nextStepResult;
        }
        else {
            // Static next step
            currentStepId = currentStep.nextStep;
        }
    }
    return result;
}
/**
 * Format the step title (question) with translation
 */
function formatStepTitle(step, answers, translateFn, formatContext, fallback) {
    try {
        const titleKey = typeof step.title === 'function'
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                step.title(answers)
            : step.title;
        const translatedTitle = translateFn(titleKey.replace('wizard:', ''));
        // Some translations might be functions that need context
        if (typeof translatedTitle === 'function') {
            // Call the function with the appropriate context
            const result = translatedTitle({
                recipient: formatContext.selectedEmployee,
                answers,
            });
            return typeof result === 'string' ? result : fallback;
        }
        if (typeof translatedTitle === 'string') {
            return translatedTitle;
        }
    }
    catch (error) {
        console.warn(`Failed to translate step title: ${fallback}`, error);
    }
    return fallback;
}
/**
 * Format the answer value with translation
 */
function formatAnswerValue(answers, answer, currentStep, _stepMap, translateFn, formatContext) {
    // Handle array answers (ChoiceList, MultiSelect)
    if (Array.isArray(answer)) {
        return answer
            .map((item) => {
            // Employee objects
            if (typeof item === 'object' &&
                item &&
                'personalInfo' in item &&
                typeof item.personalInfo === 'object' &&
                item.personalInfo !== null) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const emp = item;
                return `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`;
            }
            // Get translation key from wizard flow
            const translationKey = getOptionTranslationKey(currentStep, String(item));
            if (translationKey) {
                try {
                    const translated = translateFn(translationKey.replace('wizard:', ''));
                    if (typeof translated === 'function') {
                        return translated({
                            recipient: formatContext.selectedEmployee,
                            answers,
                        });
                    }
                    else {
                        return translated;
                    }
                }
                catch {
                    return String(item);
                }
            }
            return String(item);
        })
            .join(', ');
    }
    // Handle Employee object (single selection)
    if (typeof answer === 'object' &&
        answer !== null &&
        'personalInfo' in answer &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        typeof answer.personalInfo === 'object') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const emp = answer;
        return `${emp.personalInfo.firstName} ${emp.personalInfo.lastName}`;
    }
    // Handle Date objects
    if (answer instanceof Date || (typeof answer === 'string' && isISO(answer))) {
        return formatContext.formatDate
            ? formatContext.formatDate(answer)
            : String(answer);
    }
    // Handle numbers (Range answers)
    if (typeof answer === 'number') {
        return String(answer);
    }
    // Handle plain text (TextArea)
    if (typeof answer === 'string') {
        return answer;
    }
    // Fallback
    return String(answer);
}
/**
 * Get translation key for an option within a step
 */
function getOptionTranslationKey(step, optionId) {
    // Check if the step has static options
    if (step.component.props && 'options' in step.component.props) {
        const options = step.component.props.options;
        if (options && options.type === 'static' && Array.isArray(options.data)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const option = options.data.find((opt) => opt.id === optionId);
            return option?.title;
        }
    }
    return undefined;
}
